import {Box, Skeleton} from "@mui/material"
import {useQuery} from "@tanstack/react-query"
import {type FC} from "react"
import {Link} from "react-router-dom"
import {useConfigProvider} from "../../../config"
import {thoughtPiecesEndpoints} from "../../../services/api/thought-pieces"
import {useClientSessionStore} from "../../../stores/clientSessionStore"
import useSelectedThoughtPiecesStore from "../../../stores/fullPageThoughtPiecesStore"
import {ThoughtPiecesModel} from "../../../types/ThoughtPiecesModel"
import ImageComponent from "../../atoms/imageContainer/ImageContainer"
import {ErrorMessage} from "../../molecules/ErrorMessage"
import {ThoughtPieceItem} from "../../molecules/ThoughtPieceItem"

const ThoughtPieceOverview: FC = () => {
  const {setId} = useSelectedThoughtPiecesStore()
  const {getThoughtPieces} = thoughtPiecesEndpoints()
  const {cmsImageUrl} = useConfigProvider()
  const sessionClient = useClientSessionStore((state) => state.crmAccount)
  const {
    data: thoughtPieces,
    isLoading,
    isFetching,
    isRefetching,
    isError,
    isLoadingError,
    isRefetchError,
    refetch,
  } = useQuery<ThoughtPiecesModel[]>({
    queryKey: ["getThoughtPieces"],
    queryFn: async () => await getThoughtPieces(),
    enabled: !!sessionClient,
  })
  var borderCondition =
    !sessionClient ||
    isLoading ||
    isFetching ||
    isRefetching ||
    (!thoughtPieces && !isError && !isLoadingError && !isRefetchError)
      ? "none"
      : !thoughtPieces ||
        isError ||
        isLoadingError ||
        isRefetchError ||
        !Array.isArray(thoughtPieces) ||
        thoughtPieces.length === 0
      ? "none"
      : thoughtPieces.length > 0
      ? "solid 1px #cdcdcd"
      : "none"

  return (
    <Box
      position={"relative"}
      borderRadius={"8px"}
      sx={{
        border: borderCondition,
      }}
    >
      {!sessionClient ||
      isLoading ||
      isFetching ||
      isRefetching ||
      (!thoughtPieces && !isError && !isLoadingError && !isRefetchError)
        ? [1, 2]?.map((item, index) => (
            <Box key={`thought-pieces-overview-loader-${item}-${index}`}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  borderRadius: "8px",
                }}
              >
                <Box
                  style={{
                    paddingTop: "0px",
                    paddingLeft: "0px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Skeleton
                    animation="wave"
                    variant="rectangular"
                    width="89px"
                    height={124}
                    sx={{
                      marginTop: "-1.50rem",
                      borderTopLeftRadius: "8px",
                      borderBottomLeftRadius: "8px",
                    }}
                  />
                </Box>
                <Box
                  style={{
                    paddingTop: "0px",
                    paddingLeft: "0px",
                    width: "80%",
                    marginLeft: "8px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    style={{
                      height: "150px",
                      paddingTop: "13px",
                      paddingLeft: "0px",
                      marginLeft: "8px",
                      marginRight: "9px",
                    }}
                  >
                    <Skeleton animation="wave" variant="text" height={20} />
                    <Skeleton animation="wave" variant="text" height={20} />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width="64px"
                      height={12}
                      style={{
                        marginBottom: "10px",
                        marginTop: "10px",
                      }}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={12}
                      style={{
                        marginBottom: "4px",
                      }}
                    />
                    <Skeleton animation="wave" variant="text" height={12} />
                  </Box>
                </Box>
              </Box>
            </Box>
          ))
        : !thoughtPieces ||
          isError ||
          isLoadingError ||
          isRefetchError ||
          !Array.isArray(thoughtPieces) ||
          thoughtPieces.length === 0
        ? [1, 2]?.map((item, index) => (
            <Box key={`thought-pieces-overview-loader-${item}-${index}`}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  borderRadius: "8px",
                  filter: "blur(5px)",
                }}
              >
                <Box
                  style={{
                    paddingTop: "0px",
                    paddingLeft: "0px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Skeleton
                    animation="wave"
                    variant="rectangular"
                    width="89px"
                    height={124}
                    sx={{
                      marginTop: "-1.50rem",
                      borderTopLeftRadius: "8px",
                      borderBottomLeftRadius: "8px",
                    }}
                  />
                </Box>
                <Box
                  style={{
                    paddingTop: "0px",
                    paddingLeft: "0px",
                    width: "80%",
                    marginLeft: "8px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    style={{
                      height: "150px",
                      paddingTop: "13px",
                      paddingLeft: "0px",
                      marginLeft: "8px",
                      marginRight: "9px",
                    }}
                  >
                    <Skeleton animation="wave" variant="text" height={20} />
                    <Skeleton animation="wave" variant="text" height={20} />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width="64px"
                      height={12}
                      style={{
                        marginBottom: "10px",
                        marginTop: "10px",
                      }}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      height={12}
                      style={{
                        marginBottom: "4px",
                      }}
                    />
                    <Skeleton animation="wave" variant="text" height={12} />
                  </Box>
                </Box>
              </Box>
              <ErrorMessage
                message={"This content is not available right now"}
                messageStyle={{fontSize: "14px", color: "#595958"}}
                refetch={refetch}
              ></ErrorMessage>
            </Box>
          ))
        : thoughtPieces.map((item: ThoughtPiecesModel) => (
            <Box
              key={item.group}
              sx={{
                padding: "8px 11px 8px 8px",
              }}
            >
              <Box
                component={Link}
                to={item.itemUrl}
                onClick={() => setId(item.group)}
                sx={{
                  textDecoration: "none",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#EEEEEE",
                    cursor: "pointer",
                  },
                  '&:hover [data-parent-hover="true"] p': {
                    textDecoration: "underline 1px",
                    cursor: "pointer",
                  },
                }}
              >
                <Box
                  style={{
                    paddingTop: "0px",
                    paddingLeft: "0px",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "4px",
                  }}
                >
                  <ImageComponent
                    src={cmsImageUrl + item.imageMedium}
                    alt={item.title}
                    width="90px"
                    style={{
                      borderTopLeftRadius: "8px",
                      borderBottomLeftRadius: "8px",
                    }}
                  ></ImageComponent>
                </Box>
                <Box
                  style={{
                    paddingTop: "0px",
                    paddingLeft: "0px",
                    marginLeft: "8px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <ThoughtPieceItem
                    title={item.title}
                    subtitle={item.subtitle}
                    date={item.date}
                  ></ThoughtPieceItem>
                </Box>
              </Box>
            </Box>
          ))}
    </Box>
  )
}

export default ThoughtPieceOverview
